/**
 * User Service
 * Handles backend user profile operations and authentication state
 * Integrates with backend /api/auth/me endpoint for user profile management
 *
 * Based on: docs/integration/auth/FRONTEND_AUTH_GUIDE.md
 */

import { apiClient, getApiErrorMessage } from '../lib/api';
import { User } from '../types/user.types';

/**
 * User profile response from backend
 * Matches the structure returned by /api/auth/me endpoint
 */
export interface UserProfile {
  id?: number;
  email: string;
  /** Original IDP auth user ID for linking */
  external_id: string;
  /** Identity provider name (e.g., 'google') */
  idp_provider?: string;
  first_name: string;
  last_name: string;
  role: 'admin' | 'doctor' | 'nurse' | 'medical_assistant' | 'receptionist' | 'assistant';
  specialization?: string;
  specialty?: string;
  permissions: string[];
  clinic_id: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  avatar_url?: string;
  is_2fa_enabled: boolean;
  feature_flags?: {
    enable_live_transcription?: boolean;
    enable_worker_streaming?: boolean;
  };
  recording_limits?: {
    session_time_limit_minutes?: number;
    warning_lead_time_seconds?: number;
    source?: string;
    tier?: string;
    user_override_minutes?: number | null;
  };
}

export class UserService {
  /**
   * Fetch current user profile from backend
   * Uses the centralized apiClient which automatically injects JWT token
   *
   * @returns User profile with roles and permissions from backend
   * @throws Error if profile fetch fails or user is not authenticated
   */
  async getCurrentUser(): Promise<UserProfile> {
    try {
      console.log('[UserService] Fetching user profile from /api/auth/me...');

      const response = await apiClient.get<UserProfile>('/api/auth/me');
      if(!response?.data) console.error("No response data",response);
      console.log("RESPONSE DATA:", response);
      return response.data;
    } catch (error) {
      console.error('[UserService] ❌ Failed to fetch user profile:', error);
      const message = getApiErrorMessage(error);

      // Re-throw with context
      throw new Error(`Failed to fetch user profile: ${message}`);
    }
  }

  /**
   * Update user profile information
   * Note: Backend endpoint may have restrictions on which fields can be updated
   *
   * @param updates Partial user profile updates
   * @returns Updated user profile
   */
  async updateUserProfile(
    updates: Partial<Pick<UserProfile, 'first_name' | 'last_name' | 'specialty' | 'role' | 'avatar_url'>>
  ): Promise<UserProfile> {
    try {
      console.log('[UserService] Updating user profile...', updates);

      const response = await apiClient.patch<UserProfile>(
        '/api/auth/me',
        updates
      );

      if (!response.data) {
        console.error('[UserService] ❌ Invalid response structure:', response.data);
        throw new Error('Invalid update response from server');
      }

      const userProfile = response.data;
      console.log('[UserService] ✅ User profile updated:', userProfile.id);

      return userProfile;
    } catch (error) {
      console.error('[UserService] ❌ Failed to update user profile:', error);
      const message = getApiErrorMessage(error);

      throw new Error(`Failed to update user profile: ${message}`);
    }
  }

  /**
   * Transform backend UserProfile to frontend User type
   * Bridges the gap between backend and frontend data structures
   *
   * @param profile Backend user profile
   * @returns Frontend User object
   */
  transformProfileToUser(profile: UserProfile): User {
    return {
      id: profile.id,
      email: profile.email,
      externalId: profile.external_id,
      idpProvider: profile.idp_provider,
      firstName: profile.first_name,
      lastName: profile.last_name,
      role: profile.role,
      permissions: profile.permissions,
      clinicId: profile.clinic_id,
      isActive: profile.is_active,
      createdAt: profile.created_at,
      updatedAt: profile.updated_at,
      avatarUrl: profile.avatar_url,
      specialty: profile.specialty,
      is2faEnabled: profile.is_2fa_enabled,
      featureFlags: profile.feature_flags,
      recordingLimits: profile.recording_limits
        ? {
            sessionTimeLimitMinutes: profile.recording_limits.session_time_limit_minutes ?? 60,
            warningLeadTimeSeconds: profile.recording_limits.warning_lead_time_seconds ?? 60,
            source: profile.recording_limits.source,
            tier: profile.recording_limits.tier,
            userOverrideMinutes: profile.recording_limits.user_override_minutes ?? null,
          }
        : undefined,
    };
  }

  /**
   * Check if user has a specific permission
   *
   * @param profile User profile
   * @param permission Permission to check
   * @returns True if user has the permission
   */
  hasPermission(profile: UserProfile, permission: string): boolean {
    return profile.permissions.includes(permission);
  }

  /**
   * Check if user has any of the specified roles
   *
   * @param profile User profile
   * @param roles Roles to check
   * @returns True if user has any of the specified roles
   */
  hasAnyRole(
    profile: UserProfile,
    roles: UserProfile['role'][]
  ): boolean {
    return roles.includes(profile.role);
  }

  /**
   * Check if user is an admin
   *
   * @param profile User profile
   * @returns True if user is an admin
   */
  isAdmin(profile: UserProfile): boolean {
    return profile.role === 'admin';
  }

  /**
   * Initiate 2FA enablement
   */
  async enable2FA(): Promise<any> {
    return (await apiClient.post('/api/auth/2fa/enable')).data;
  }

  /**
   * Confirm 2FA enablement
   */
  async confirm2FA(code: string): Promise<any> {
    return (await apiClient.post('/api/auth/2fa/confirm', { otp_code: code })).data;
  }

  /**
   * Disable 2FA
   */
  async disable2FA(): Promise<any> {
    return (await apiClient.post('/api/auth/2fa/disable')).data;
  }

  /**
   * Start avatar upload - get presigned URL
   */
  async startAvatarUpload(filename: string, mimeType: string): Promise<{ upload_url: string; avatar_url: string }> {
    const response = await apiClient.post('/api/auth/me/avatar/start-upload', {
      filename,
      mime_type: mimeType
    });
    return response.data;
  }

  /**
   * Upload avatar to S3
   */
  async uploadAvatarToS3(url: string, file: File): Promise<void> {
    const response = await fetch(url, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      }
    });

    if (!response.ok) {
      throw new Error(`Avatar upload failed: ${response.status} ${response.statusText}`);
    }
  }

  /**
   * Delete current user account (soft delete)
   */
  async deleteAccount(): Promise<void> {
    try {
      await apiClient.delete('/api/auth/me');
    } catch (error) {
      console.error('[UserService] ❌ Failed to delete account:', error);
      const message = getApiErrorMessage(error);
      throw new Error(`Failed to delete account: ${message}`);
    }
  }
}

// Export singleton instance
export const userService = new UserService();
